import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../common/hooks/hooks";
import { RootState } from "../../../../../../app/store";
import { setCurrentStep } from "../../../checkoutSlice";
import { useUpdateCustomer } from "../../../../cart/hooks/useUpdateCustomer";
import { useGetCheckoutDataQuery } from "../../../api/checkoutApi";
import { useGetCartQuery } from "../../../../cart/api/cartApi";
import {
  createShippingSchema,
  ShippingFormData,
} from "../../../helpers/checkoutValidation";
import { useUnsavedChangesWarning } from "../../../../../../common/hooks/useUnsavedChangesWarning";
import CheckoutDetailsForm from "../../common/CheckoutDetailsForm/CheckoutDetailsForm";
import CheckoutStepLayout from "../../common/CheckoutStepLayout";
import ShippingMethods from "./ShippingMethods";
import UseBillingForShippingToggle from "../../common/UseBillingForShippingToggle";

const ShippingStep: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigationDirection = useRef<"prev" | "next">("next");

  const useBillingForShipping = useAppSelector(
    (state: RootState) => state.checkout.useBillingForShipping,
  );

  const { data: checkoutData } = useGetCheckoutDataQuery();
  const { data: cartData } = useGetCartQuery();

  const { handleUpdateCustomer, isUpdatingCustomer } = useUpdateCustomer();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors, isDirty, isSubmitting },
    reset,
  } = useForm<ShippingFormData>({
    resolver: zodResolver(createShippingSchema(t)),
    mode: "onSubmit",
    defaultValues: {
      ...(!useBillingForShipping
        ? checkoutData?.shippingAddress
        : checkoutData?.billingAddress),
      country: checkoutData?.shippingAddress?.country || "",
    },
  });

  useUnsavedChangesWarning(isDirty, isSubmitting);

  const watchedAddress = watch(["country", "state", "city", "postcode"]);
  const lastSyncedPayload = useRef<string>("");

  useEffect(() => {
    if (useBillingForShipping) return;

    const [country] = watchedAddress;
    if (!country) return;

    const timer = setTimeout(() => {
      const currentValues = getValues();
      const serverShipping = checkoutData?.shippingAddress;

      const isDifferent =
        currentValues.country !== serverShipping?.country ||
        currentValues.state !== serverShipping?.state ||
        currentValues.city !== serverShipping?.city ||
        currentValues.postcode !== serverShipping?.postcode;

      if (!isDifferent) return;

      const payload = {
        shipping_address: {
          country: currentValues.country,
          state: currentValues.state,
          city: currentValues.city,
          postcode: currentValues.postcode,
          address_1: currentValues.address1,
          address_2: currentValues.address2,
        },
      };

      const payloadString = JSON.stringify(payload);

      if (payloadString !== lastSyncedPayload.current) {
        lastSyncedPayload.current = payloadString;
        handleUpdateCustomer(payload).catch((err) =>
          console.debug("Background shipping update skipped", err),
        );
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [
    watchedAddress[0],
    watchedAddress[1],
    watchedAddress[2],
    watchedAddress[3],
    useBillingForShipping,
    handleUpdateCustomer,
    checkoutData,
    getValues,
  ]);

  const prevUseBillingForShipping = useRef(useBillingForShipping);

  useEffect(() => {
    if (checkoutData) {
      const hasModeChanged =
        prevUseBillingForShipping.current !== useBillingForShipping;

      if (hasModeChanged || !isDirty) {
        const addressData = !useBillingForShipping
          ? checkoutData.shippingAddress
          : checkoutData.billingAddress;

        reset({
          ...addressData,
          country: addressData.country || "",
        });
      }
    }
    prevUseBillingForShipping.current = useBillingForShipping;
  }, [checkoutData, useBillingForShipping, reset, isDirty]);

  const onPrevClick = () => {
    navigationDirection.current = "prev";
  };

  const onNextClick = () => {
    navigationDirection.current = "next";
  };

  const onSubmit = async (data: ShippingFormData) => {
    const hasSelectedRate = cartData?.shippingRates?.some(
      (rate) => rate.selected,
    );

    if (!hasSelectedRate) {
      toast.error(
        t(
          "feedback.cart.shippingRate.selectShippingRateError",
          "Please select a shipping method",
        ),
      );
      return;
    }

    let payload: any;

    if (!useBillingForShipping) {
      payload = {
        shipping_address: {
          first_name: data.firstName,
          last_name: data.lastName,
          company: data.company,
          country: data.country,
          address_1: data.address1,
          address_2: data.address2,
          city: data.city,
          state: data.state,
          postcode: data.postcode,
          phone: data.phone,
        },
      };
    } else {
      const billing = checkoutData?.billingAddress;
      payload = {
        shipping_address: {
          first_name: billing?.firstName,
          last_name: billing?.lastName,
          company: billing?.company,
          country: billing?.country,
          address_1: billing?.address1,
          address_2: billing?.address2,
          city: billing?.city,
          state: billing?.state,
          postcode: billing?.postcode,
          phone: billing?.phone,
        },
      };
    }

    const result = await handleUpdateCustomer(payload);

    if (result.success) {
      reset(data);

      const hasAppointment =
        checkoutData?.appointment?.needsAppointment ||
        cartData?.appointment?.needsAppointment ||
        false;

      if (navigationDirection.current === "prev") {
        dispatch(setCurrentStep("billing"));
      } else {
        if (hasAppointment) {
          dispatch(setCurrentStep("appointment"));
        } else {
          dispatch(setCurrentStep("payment"));
        }
      }
    }
  };

  const onError = () => {
    if (!useBillingForShipping) {
      toast.error(
        t(
          "feedback.cart.customer.missingFieldBackend",
          "Please fill in all required fields.",
        ),
      );
    } else {
      toast.error(
        t(
          "checkoutPage.billingInvalid",
          "Billing address details are incomplete. Please go back and check them.",
        ),
      );
    }
  };

  if (cartData && !cartData.needsShipping) return null;

  return (
    <CheckoutStepLayout
      title={t("shippingStep.stepTitle", "Shipping Details")}
      onSubmit={handleSubmit(onSubmit, onError)}
      isProcessing={isUpdatingCustomer}
      nextButtonText={t("checkoutForm.actions.continue", "Continue")}
      isNextDisabled={false}
      showPrevButton={true}
      prevButtonType="submit"
      onPrevious={onPrevClick}
      showNextButton={true}
      nextButtonType="submit"
      onNext={onNextClick}
    >
      <UseBillingForShippingToggle />

      <div className="checkout-step__section">
        <CheckoutDetailsForm
          register={register}
          control={control}
          errors={errors}
          setValue={setValue}
          watch={watch}
          addressType="shipping"
          disabled={useBillingForShipping}
        />
      </div>

      <div className="checkout-step__section">
        <ShippingMethods />
      </div>
    </CheckoutStepLayout>
  );
};

export default ShippingStep;
